import React, { useState, useEffect } from 'react';
import { useApi } from '../../../lib/api';
import { SearchableSelect } from '../filters/FilterDropdowns';
import '../../../styles/Performance.css';

export function GoalForm({
  appId,
  goal = null,
  roleId,
  roles: rolesProp = [],
  onSave,
  onCancel,
  totalWeightage = 0,
}) {
  const api = useApi();
  const [formData, setFormData] = useState({
    goal_name: goal?.goal_name || '',
    description: goal?.description || '',
    target_l1: goal?.target_l1 || '',
    target_l2: goal?.target_l2 || '',
    target_l3: goal?.target_l3 || '',
    target_l4: goal?.target_l4 || '',
    target_l5: goal?.target_l5 || '',
    weightage: goal?.weightage || 0,
  });
  const [selectedRoleId, setSelectedRoleId] = useState(goal?.role_id || roleId || '');
  const [roles, setRoles] = useState(rolesProp);
  const [rolesLoading, setRolesLoading] = useState(false);

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (goal) return; // editing — no need to fetch roles
    if (rolesProp.length > 0) {
      setRoles(rolesProp);
      if (!selectedRoleId && rolesProp.length === 1) setSelectedRoleId(rolesProp[0].id);
      return;
    }
    // Fetch roles directly if parent didn't provide them
    setRolesLoading(true);
    api.listMyRoleAssignments(appId)
      .then(data => {
        const items = (data.items || [])
          .filter(a => a.role)
          .map(a => ({ id: a.role_id, title: a.role.title, code: a.role.code }));
        setRoles(items);
        if (items.length === 1) setSelectedRoleId(items[0].id);
      })
      .catch(() => {})
      .finally(() => setRolesLoading(false));
  }, [appId, goal]);

  const remainingWeightage = 100 - totalWeightage;
  const maxAllowed = remainingWeightage + (goal?.weightage || 0);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (!formData.goal_name.trim()) {
      setError('Goal name is required');
      return;
    }
    if (!selectedRoleId) {
      setError('Please select a role');
      return;
    }

    if (maxAllowed <= 0) {
      setError('No weightage available. Total is already 100%.');
      return;
    }
    if (!formData.weightage || formData.weightage <= 0 || formData.weightage > maxAllowed) {
      setError(`Weightage must be between 1 and ${maxAllowed}`);
      return;
    }

    setLoading(true);
    try {
      if (goal) {
        await api.updateGoal(appId, goal.id, formData);
      } else {
        await api.createGoal(appId, {
          ...formData,
          role_id: selectedRoleId,
        });
      }
      onSave?.(selectedRoleId);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save goal');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form className="goal-form" onSubmit={handleSubmit}>
      {error && <div className="error-message">{error}</div>}
      {!goal && (
        <div className="form-group">
          <label>Role <span style={{ color: '#ef4444' }}>*</span></label>
          {rolesLoading ? (
            <input type="text" value="Loading roles…" disabled style={{ background: 'var(--bg-input, #1e2330)', color: 'var(--text-secondary)' }} />
          ) : roles.length === 0 ? (
            <input type="text" value="No role assigned — contact your admin" disabled style={{ background: 'var(--bg-input, #1e2330)', color: 'var(--text-secondary)' }} />
          ) : roles.length === 1 ? (
            <input type="text" value={roles[0].title} disabled style={{ background: 'var(--bg-input, #1e2330)', color: 'var(--text-secondary)' }} />
          ) : (
            <SearchableSelect
              value={selectedRoleId}
              onChange={v => setSelectedRoleId(v ? parseInt(v) : '')}
              options={[{ value: '', label: '— Select role —' }, ...roles.map(r => ({ value: r.id, label: r.title }))]}
              showAll={false}
              disabled={loading}
              btnClass="form-select-btn"
              matchTriggerWidth
            />
          )}
        </div>
      )}
      <div className="form-group">
        <label>Goal Name <span style={{ color: '#ef4444' }}>*</span></label>
        <input
          type="text"
          value={formData.goal_name}
          onChange={e => handleChange('goal_name', e.target.value.slice(0, 1000))}
          maxLength={1000}
          placeholder="e.g., Increase customer satisfaction"
          disabled={loading}
        />
      </div>

      <div className="form-group">
        <label>Description</label>
        <textarea
          value={formData.description}
          onChange={e => handleChange('description', e.target.value.slice(0, 1000))}
          placeholder="Describe what you want to achieve..."
          rows="3"
          maxLength={1000}
          disabled={loading}
        />
        <span style={{ fontSize: 11, color: (formData.description?.length || 0) >= 1000 ? 'var(--danger)' : 'var(--text-muted)' }}>
          {formData.description?.length || 0}/1000
        </span>
      </div>

      <div className="form-row">
        <div className="form-group">
          <label>Weightage (%) <span style={{ color: '#ef4444' }}>*</span></label>
          <input
            type="number"
            min="0.5"
            max={maxAllowed > 0 ? maxAllowed : undefined}
            step="0.5"
            value={formData.weightage}
            onChange={e => handleChange('weightage', parseFloat(e.target.value))}
            disabled={loading || maxAllowed <= 0}
          />
          <small style={{ color: 'var(--text-muted)' }}>
            {maxAllowed > 0 ? `Max available: ${maxAllowed}%` : 'No weightage available (100% already allocated)'}
          </small>
        </div>
      </div>

      <div className="form-group">
        <label>Targets (L1–L5)</label>
        {[1, 2, 3, 4, 5].map((level, idx) => (
          <div key={level} className="target-row" style={idx > 0 ? { marginTop: 6 } : {}}>
            <label>L{level}</label>
            <input
              type="text"
              value={formData[`target_l${level}`]}
              onChange={e => handleChange(`target_l${level}`, e.target.value)}
              placeholder={`Level ${level} target`}
              maxLength={200}
              disabled={loading}
            />
          </div>
        ))}
      </div>

      <div className="form-actions">
        <button type="submit" className="btn-primary" disabled={loading}>
          {loading ? 'Saving...' : goal ? 'Update Goal' : 'Create Goal'}
        </button>
        <button type="button" className="btn-secondary" onClick={onCancel} disabled={loading}>
          Cancel
        </button>
      </div>
    </form>
  );
}

